// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import moment from "moment";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";

// ANT COMPONENTS
import { ArrowLeftOutlined, LoadingOutlined } from "@ant-design/icons";
import { Empty, Avatar, Row, Col, Input, Spin } from "antd";
import { AlertBox, BasicModal, Button } from "@life-line-lab/components-react";

// STYLE
import "./customer-profile-details.scss";
import "./../customer-management.scss";

// TYPES
import {
  CustomerApprovalStatus,
  CustomerCompanyFormInputType,
  CustomerType,
  OrgType,
} from "../../../types/customer-form.types";
import { ModalType } from "Types/common.types";

// HELPERS
import { encode } from "Helpers/enc";

// JSON LIST
import countryList from "../../../data/country.json";

// ATOM
import { customerAtom, CustomerAtomType } from "Recoil/customer.atom";
import { useSession } from "Hooks/index";

interface CustomerProfileDetailsComponentProps {
  onBack: () => void;
  onDeleteCustomer: (orgID: string, tempDeletedReason: string) => void;
  onRestoreCustomer: (orgID: string) => void;
  onFetchCustomer: () => void;
  customerDetails: Partial<CustomerCompanyFormInputType>;
  canDelete: boolean;
  canViewList: boolean;
  canUpdate: boolean;
  handleGenerateBearerToken: () => void
  isLoading?: boolean;
}

const CustomerProfileDetailsComponent: React.FC<CustomerProfileDetailsComponentProps> = ({
  onBack,
  onDeleteCustomer,
  onRestoreCustomer,
  onFetchCustomer,
  customerDetails,
  canDelete,
  canViewList,
  canUpdate,
  handleGenerateBearerToken,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { roles } = useSession();

  const initialModal: ModalType<Partial<CustomerCompanyFormInputType>> = {
    open: false,
    record: {},
    type: "delete",
  };
  const initialError = {};
  const initialInput = { reason: "" };

  const [modal, setModal] =
    useState<ModalType<Partial<CustomerCompanyFormInputType>>>(initialModal);
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [customerStatus, setCustomerStatus] = useRecoilState<CustomerAtomType>(customerAtom);

  useEffect(() => {
    if (customerStatus.delete === "success") {
      resetModalState();
      onFetchCustomer();
    }
    if (customerStatus.restore === "success") {
      resetModalState();
      setCustomerStatus((prevStatus) => ({ ...prevStatus, restore: "idle" }));
      setTimeout(() => {
        history.push("/customer-management/profile");
      }, 250);
    }
  }, [customerStatus]);

  const confirmDelete = () => {
    if (input.reason.length === 0) {
      setError({
        ...error,
        reason: t("error.required", { field: t(`label.reason`) }),
      });
    } else {
      onDeleteCustomer(customerDetails._id || "", input.reason);
    }
  };

  const confirmRestore = () => {
    onRestoreCustomer(customerDetails._id || "");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleEdit = () => {
    if (customerDetails._id) {
      const customerID = encode(customerDetails._id);
      history.push(
        `/customer-management/profile/edit/${customerDetails.customerType?.toLowerCase()}/${customerID}`
      );
    }
  };

  const handleCustomerSetting = () => {
    history.push(`/order-management/customer/${id}/settings`);
  };

  const handleDisplayBearerToken = () => {
    if (isLoading)
      return (<LoadingOutlined/>)
    else
      return (<Button
        type="success"
        className="mr-10"
        label={t("action.generate")}
        onClick={() => {
          handleGenerateBearerToken()
        }}
      />)
  }

  const resetModalState = () => {
    setModal(initialModal);
    setInput(initialInput);
    setError(initialError);
  };

  return (
    <div className="p-20 my-20 customer-profile-details-container">
      <div className="main-header">
        {canViewList ? (
          <span
            className="cursor-pointer back-action"
            onClick={() => {
              onBack();
            }}
          >
            <ArrowLeftOutlined />
            <span className="ml-5">{t("txt.back")}</span>
          </span>
        ) : null}
        <span className="center header-title">
          {t("title.customerDetails").toUpperCase()} | {customerDetails.code}
        </span>
        {roles[0].type == "COMPANY" && customerDetails?.tempDeleteInfo?.tempDelete == false && (
          <Button
            type="default"
            className="mr-10 order-processing-setup"
            label={t("title.orderProcessingSetup")}
            onClick={() => {
              handleCustomerSetting();
            }}
          />
        )}
      </div>
      <Avatar
        className="center my-40"
        size={128}
        src={
          (customerDetails?.file && customerDetails?.file.file_url) || Empty.PRESENTED_IMAGE_DEFAULT
        }
      />
      <Row gutter={[15, 15]} className="customer-details-container">
        <Col xs={24} sm={24} md={12} xl={6}>
          <div className="bold-label">{t("label.customerName").toUpperCase()}</div>
          <div className="fs-13">{customerDetails?.customerName}</div>
        </Col>
        {customerDetails.customerType === CustomerType.COMPANY ? (
          <Col xs={24} sm={24} md={12} xl={6}>
            <div className="bold-label">{t("label.registrationNo").toUpperCase()}</div>
            <div className="fs-13">
              {customerDetails?.businessRegistrationNumber || <span>&#11835;</span>}
            </div>
          </Col>
        ) : (
          <></>
        )}
        <Col xs={24} sm={24} md={12} xl={6}>
          <div className="bold-label">{t("label.mobileNo")}</div>
          <div className="fs-13">
            {customerDetails.customerType === CustomerType.COMPANY
              ? `+${customerDetails?.contactNo?.dialCode} 
                            ${customerDetails?.contactNo?.number}`
              : `+${customerDetails?.mobileNo?.dialCode} 
                            ${customerDetails?.mobileNo?.number}`}
          </div>
        </Col>
        <Col xs={24} sm={24} md={12} xl={6}>
          <div className="bold-label">{t("label.emailAddress").toUpperCase()}</div>
          <div className="fs-13">{customerDetails?.emailAddress}</div>
        </Col>
        <Col xs={24} sm={24} md={12} xl={6}>
          <div className="bold-label">{t("label.registeredAddress").toUpperCase()}</div>
          <div className="address-line-break fs-13">
            <div>
              {customerDetails.street1 + ", "}
              {customerDetails.street2 ? customerDetails.street2 + ", " : ""}
            </div>
            <div>
              {customerDetails.postcode + " "}
              {customerDetails.city + ", "}
            </div>
            <div>{customerDetails.state + ", "}</div>
            <div>
              {countryList.find((country) => {
                return country.Code === customerDetails.country;
              })?.Name || ""}
            </div>
          </div>
        </Col>
        {customerDetails.customerType === CustomerType.COMPANY ? (
          <Col xs={24} sm={24} md={12} xl={6}>
            <div className="bold-label">{t("label.companyWebsite").toUpperCase()}</div>
            <div className="fs-13">{customerDetails?.companyWebsite || <span>&#11835;</span>}</div>
          </Col>
        ) : (
          <></>
        )}
        {customerDetails.tempDeleteInfo?.tempDelete ? (
          <Col xs={24} sm={24} md={12} xl={6}>
            <div className="bold-label">{t("label.deleteReason").toUpperCase()}</div>
            <div className="fs-13">{customerDetails.tempDeleteInfo.tempDeletedReason}</div>
          </Col>
        ) : customerDetails.customerType === CustomerType.COMPANY ? (
          <Col xs={24} sm={24} md={12} xl={6}>
            <div className="bold-label">{t("label.operationalHours").toLocaleUpperCase()}</div>
            <div className="fs-13">
              {customerDetails?.workingHours &&
                (!isEmpty(customerDetails?.workingHours) ? (
                  customerDetails?.workingHours?.map((hour, index) => {
                    return (
                      <Row key={index}>
                        <Col span={8} style={{ marginBottom: 0 }}>
                          {hour?.day?.charAt(0)}
                          {hour?.day?.substring(1).toLowerCase()}:
                        </Col>
                        <Col span={16} style={{ marginBottom: 0 }}>
                          {moment(hour?.openingTime).format("hh:mm a")} -{" "}
                          {moment(hour?.closingTime).format("hh:mm a")}
                        </Col>
                      </Row>
                    );
                  })
                ) : (
                  <div>{t("txt.noOperationalHours")}</div>
                ))}
            </div>
          </Col>
        ) : (
          <></>
        )}
        <Col xs={24} sm={24} md={12} xl={6}>
          <div className="bold-label">{t("label.prefix").toUpperCase()}</div>
          <div className="address-line-break fs-13">{customerDetails?.prefix}</div>
        </Col>
      </Row>
      {customerDetails.customerType === CustomerType.COMPANY ? (
        <React.Fragment>
          <div className="center my-40 header-title">
            {t("title.customerAdminDetails").toUpperCase()}
          </div>
          <Row gutter={[15, 15]} className="customer-admin-details-container">
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">{t("label.contactPerson").toUpperCase()}</div>
              <div className="fs-13">{customerDetails?.contactPerson || <span>&#11835;</span>}</div>
            </Col>
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">{t("label.emailAddress").toUpperCase()}</div>
              <div className="fs-13">
                {customerDetails?.adminEmailAddress || <span>&#11835;</span>}
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">{t("label.contactNo").toUpperCase()}</div>
              <div className="fs-13">{`+${customerDetails?.adminContactNo?.dialCode} 
                                    ${customerDetails?.adminContactNo?.number}`}</div>
            </Col>
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">{t("label.officeContactNo").toUpperCase()}</div>
              <div className="fs-13">
                {!isEmpty(customerDetails.officeContactNo) ? (
                  `+${customerDetails?.officeContactNo?.dialCode} 
                                        ${customerDetails?.officeContactNo?.number}`
                ) : (
                  <span>&#11835;</span>
                )}
              </div>
            </Col>
          </Row>
        </React.Fragment>
      ) : null}
      {canUpdate ? (
        !customerDetails.tempDeleteInfo?.tempDelete ? (
          customerDetails.approvalStatus &&
          customerDetails.approvalStatus === CustomerApprovalStatus.APPROVED ? (
            <div className="right mt-50">
              {handleDisplayBearerToken()}
              <Button
                type="primary"
                className="mr-10"
                label={t("action.edit")}
                onClick={() => {
                  handleEdit();
                }}
              />
              {canDelete ? (
                <Button
                  type="danger"
                  label={t("action.delete")}
                  onClick={() => {
                    setModal({
                      ...modal,
                      open: true,
                      type: "delete",
                      record: customerDetails,
                    });
                  }}
                />
              ) : null}
            </div>
          ) : null
        ) : (
          <div className="right mt-50">
            <Button
              type="primary"
              label={t("action.restore")}
              onClick={() => {
                setModal({ ...modal, open: true, type: "restore", record: customerDetails });
              }}
            />
          </div>
        )
      ) : null}
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          resetModalState();
        }}
      >
        {modal.type === "restore" ? (
          <div className="customer-restore-modal">
            <div className="modal-title">{t("label.restoreCustomerAccount").toUpperCase()}</div>
            <div>{t("txt.areYouSureRestoreAccount")}</div>
            <div>
              <b>{t("txt.customer").toUpperCase()}:</b> {modal.record?.customerName || ""}
            </div>
            <div className="mt-30 right">
              <Button
                type="success"
                label={t("action.confirm")}
                onClick={() => {
                  confirmRestore();
                }}
              />
              <Button
                type="success"
                label={t("action.cancel")}
                className="btn--ghost"
                onClick={() => {
                  resetModalState();
                }}
              />
            </div>
          </div>
        ) : (
          <div className="customer-delete-modal">
            <div className="modal-title">{t("label.deleteCustomer").toUpperCase()}</div>
            <div>{t("txt.areYouSureDeleteAccount")}</div>
            <div>
              <b>{t("txt.customer").toUpperCase()}:</b> {modal.record?.customerName || ""}
            </div>
            <div>
              <label className="inp-label inp-label--required">{t("label.enterReason")}</label>
              <Input.TextArea
                id="message"
                size="middle"
                rows={3}
                name="reason"
                value={input.reason}
                onChange={handleChange}
                className="mt-5"
                required={true}
                placeholder={t("label.enterHere")}
              />
              <AlertBox text={error.reason} />
            </div>
            <div className="mt-30 right">
              <Button
                type="danger"
                label={t("action.delete")}
                onClick={() => {
                  confirmDelete();
                }}
              />
              <Button
                type="danger"
                label={t("action.cancel")}
                className="btn--ghost"
                onClick={() => {
                  resetModalState();
                }}
              />
            </div>
          </div>
        )}
      </BasicModal>
    </div>
  );
};

export default CustomerProfileDetailsComponent;
